import React, { useEffect, useState, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Layers, Users, LayoutGrid, Globe, Inbox, DollarSign, CreditCard,
  Handshake, ArrowLeft, Sparkles, Send, Loader2, LogOut,
  UserPlus, Shield, Copy, Trash2, Search, RefreshCw, Mail, FlaskConical,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const StatCard = ({ icon: Icon, label, value, testid, accent }) => (
  <div data-testid={testid} className={`bg-white border p-5 shadow-hard ${accent ? "border-[#ff3b00]" : "border-[#0a2540]"}`}>
    <div className="flex items-center justify-between">
      <span className="label-tech text-[#475569]">{label}</span>
      <Icon size={16} className={accent ? "text-[#ff3b00]" : "text-[#0a2540]"} />
    </div>
    <div className="font-heading font-black text-3xl mt-2">{value}</div>
  </div>
);

const ROLE_COLORS = {
  superadmin: "bg-[#ff3b00] text-white",
  admin: "bg-[#0a2540] text-white",
  beta_tester: "bg-[#0055ff] text-white",
  customer: "bg-gray-200 text-[#0a2540]",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const isSuper = user?.role === "superadmin";
  const [section, setSection] = useState("team");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isSuper) {
      api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {});
      setSection("overview");
    }
  }, [isSuper]);

  if (loading) return <div className="min-h-screen bg-[#f2f0eb]" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.features?.staff) return <Navigate to="/dashboard" replace />;

  const tabs = [
    ...(isSuper ? [{ id: "overview", label: "Overview" }] : []),
    { id: "team", label: "Team & members" },
    { id: "projects", label: "Projects" },
  ];

  return (
    <div className="min-h-screen bg-[#f2f0eb] text-[#0a2540]" data-testid="admin-dashboard">
      <header className="bg-[#0a2540] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#ff3b00] flex items-center justify-center"><Layers size={16} /></div>
            <span className="font-heading font-black text-lg">BuildForged</span>
            <span className="label-tech bg-[#ff3b00] px-2 py-0.5" style={{ color: "#fff" }}>{isSuper ? "OWNER" : "ADMIN"}</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/dashboard" data-testid="admin-to-dashboard" className="label-tech hover:text-[#ff3b00] flex items-center gap-1.5"><ArrowLeft size={13} /> My studio</Link>
            <button onClick={logout} data-testid="admin-logout" className="label-tech hover:text-[#ff3b00] flex items-center gap-1.5"><LogOut size={13} /> Log out</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="label-tech mb-1">/ {isSuper ? "OWNER CONTROL ROOM" : "TEAM MANAGEMENT"}</div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl mb-6">{isSuper ? "Platform command" : "Manage your team"}</h1>

        <div className="flex gap-2 mb-8 border-b border-[#0a2540]/20">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setSection(t.id)}
              className={`px-4 py-2.5 font-tech uppercase tracking-wider text-sm border-b-2 -mb-px transition-colors ${section === t.id ? "border-[#ff3b00] text-[#ff3b00]" : "border-transparent text-[#475569] hover:text-[#0a2540]"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {section === "overview" && isSuper && <Overview data={data} />}
        {section === "team" && <TeamPanel isSuper={isSuper} meId={user.id} />}
        {section === "projects" && <ProjectsPanel />}
      </main>
    </div>
  );
}

function Overview({ data }) {
  const s = data?.stats;
  const maxSignup = Math.max(1, ...(s?.signups_series || []).map((d) => d.count));
  const [msgs, setMsgs] = useState([{ role: "assistant", content: "Hi 👋 I'm your Admin Copilot. Ask me about growth, revenue, users, or how to modify your sites — I have your live platform metrics." }]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, thinking]);

  const ask = async () => {
    const t = input.trim();
    if (!t || thinking) return;
    setInput("");
    const history = msgs;
    setMsgs((m) => [...m, { role: "user", content: t }]);
    setThinking(true);
    try {
      const { data: r } = await api.post("/admin/assistant", { message: t, history });
      setMsgs((m) => [...m, { role: "assistant", content: r.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "Copilot is unavailable right now." }]);
    } finally { setThinking(false); }
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="stat-revenue" icon={DollarSign} label="Total revenue" value={`$${s?.revenue_total ?? "—"}`} accent />
        <StatCard testid="stat-users" icon={Users} label="Users" value={s?.users_total ?? "—"} />
        <StatCard testid="stat-projects" icon={LayoutGrid} label="Projects" value={s?.projects_total ?? "—"} />
        <StatCard testid="stat-published" icon={Globe} label="Published" value={s?.published_total ?? "—"} />
        <StatCard testid="stat-subs" icon={CreditCard} label="Active subs" value={s?.active_subscriptions ?? "—"} />
        <StatCard testid="stat-leads" icon={Inbox} label="Leads captured" value={s?.leads_total ?? "—"} />
        <StatCard testid="stat-handoffs" icon={Handshake} label="Paid hand-offs" value={s?.handoffs_paid ?? "—"} />
        <StatCard testid="stat-plans" icon={Layers} label="Paying (Pro+)" value={s ? (s.users_by_plan?.pro || 0) + (s.users_by_plan?.agency || 0) + (s.users_by_plan?.starter || 0) : "—"} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-[#0a2540] p-6 shadow-hard">
            <div className="label-tech mb-4">/ SIGNUPS — LAST 14 DAYS</div>
            <div className="flex items-end gap-1.5 h-40">
              {(s?.signups_series || []).map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center justify-end group" title={`${d.date}: ${d.count}`}>
                  <div className="w-full bg-[#ff3b00] transition-all hover:bg-[#e63500]" style={{ height: `${(d.count / maxSignup) * 100}%`, minHeight: d.count ? "4px" : "0" }} />
                  <span className="text-[9px] text-[#475569] mt-1 rotate-45 origin-left">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-[#0a2540] text-white flex flex-col shadow-hard h-[420px]" data-testid="admin-copilot">
          <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
            <Sparkles size={16} className="text-[#ff3b00]" />
            <span className="font-heading font-bold">Admin Copilot</span>
          </div>
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 rounded-lg text-sm leading-snug whitespace-pre-wrap ${m.role === "user" ? "bg-[#ff3b00] text-white" : "bg-white/10 text-white/90"}`}>{m.content}</div>
              </div>
            ))}
            {thinking && <div className="text-white/50 text-xs px-1">Copilot is thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input data-testid="admin-copilot-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="Ask about growth, revenue, edits…" className="flex-1 bg-white/10 text-white placeholder:text-white/40 px-3 py-2.5 text-sm outline-none focus:bg-white/15" />
            <button data-testid="admin-copilot-send" onClick={ask} disabled={thinking} className="bg-[#ff3b00] text-white px-3 disabled:opacity-60 hover:bg-[#e63500] transition-colors">{thinking ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}</button>
          </div>
        </div>
      </div>
    </>
  );
}

function TeamPanel({ isSuper, meId }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [invites, setInvites] = useState([]);
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("beta_tester");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);

  const roleOptions = isSuper
    ? ["customer", "beta_tester", "admin", "superadmin"]
    : ["customer", "beta_tester"];
  const inviteRoleOptions = isSuper ? ["beta_tester", "customer", "admin"] : ["beta_tester", "customer"];

  const loadUsers = () => api.get("/admin/users", { params: { search } }).then((r) => setUsers(r.data.users)).catch(() => {});
  const loadInvites = () => api.get("/admin/invites").then((r) => setInvites(r.data.invites)).catch(() => {});
  const loadSummary = () => api.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});

  useEffect(() => { loadUsers(); loadInvites(); loadSummary(); }, []); // eslint-disable-line

  const changeRole = async (uid, role) => {
    try {
      await api.post(`/admin/users/${uid}/role`, { role });
      setUsers((us) => us.map((u) => (u.id === uid ? { ...u, role } : u)));
      loadSummary();
      toast.success("Role updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not change role");
      loadUsers();
    }
  };

  const createInvite = async () => {
    if (!invEmail.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { email: invEmail.trim(), role: invRole });
      setInvEmail("");
      loadInvites();
      loadSummary();
      const link = `${window.location.origin}/register?invite=${data.token}`;
      navigator.clipboard?.writeText(link).catch(() => {});
      toast.success("Invite created — link copied to clipboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create invite");
    } finally { setBusy(false); }
  };

  const deleteInvite = async (token) => {
    await api.delete(`/admin/invites/${token}`);
    setInvites((iv) => iv.filter((i) => i.token !== token));
    loadSummary();
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/register?invite=${token}`);
    toast.success("Invite link copied");
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="team-stat-users" icon={Users} label="Total users" value={summary?.users_total ?? "—"} />
        <StatCard testid="team-stat-admins" icon={Shield} label="Admins" value={summary?.admins ?? "—"} />
        <StatCard testid="team-stat-beta" icon={FlaskConical} label="Beta testers" value={summary?.beta_testers ?? "—"} accent />
        <StatCard testid="team-stat-invites" icon={Mail} label="Pending invites" value={summary?.pending_invites ?? "—"} />
      </div>

      {/* Invite */}
      <div className="bg-white border border-[#0a2540] shadow-hard p-5" data-testid="invite-card">
        <div className="label-tech mb-3 flex items-center gap-1.5"><UserPlus size={13} /> INVITE BY EMAIL</div>
        <p className="text-sm text-[#475569] mb-3">Send someone a link to join as a beta tester or admin. When they sign up through it, the role is applied automatically.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input data-testid="invite-email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} placeholder="person@email.com" className="flex-1 bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]" />
          <select data-testid="invite-role" value={invRole} onChange={(e) => setInvRole(e.target.value)} className="bg-white border border-[#d3d3d3] p-2.5 text-sm outline-none focus:border-[#ff3b00]">
            {inviteRoleOptions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button data-testid="invite-submit" onClick={createInvite} disabled={busy} className="bg-[#ff3b00] text-white font-semibold px-5 py-2.5 hover:bg-[#e63500] disabled:opacity-60 transition-colors">Create invite</button>
        </div>
        {invites.length > 0 && (
          <div className="mt-4 space-y-2">
            {invites.map((iv) => (
              <div key={iv.token} data-testid={`invite-row-${iv.email}`} className="flex items-center justify-between gap-2 border border-[#d3d3d3] px-3 py-2 text-sm">
                <div className="min-w-0">
                  <span className="font-semibold truncate">{iv.email}</span>
                  <span className={`ml-2 label-tech px-1.5 py-0.5 ${ROLE_COLORS[iv.role] || ""}`}>{iv.role}</span>
                  <span className="ml-2 label-tech opacity-60">{iv.accepted ? "ACCEPTED" : "PENDING"}</span>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {!iv.accepted && <button onClick={() => copyLink(iv.token)} title="Copy invite link" className="p-1.5 border border-[#d3d3d3] hover:border-[#0055ff] hover:text-[#0055ff]"><Copy size={13} /></button>}
                  <button onClick={() => deleteInvite(iv.token)} title="Delete" className="p-1.5 border border-[#d3d3d3] hover:border-[#ff3b00] hover:text-[#ff3b00]"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Users */}
      <div className="bg-white border border-[#0a2540] shadow-hard p-5" data-testid="users-card">
        <div className="flex items-center justify-between mb-3">
          <div className="label-tech flex items-center gap-1.5"><Users size={13} /> ALL USERS</div>
          <button onClick={loadUsers} className="label-tech flex items-center gap-1 hover:text-[#ff3b00]"><RefreshCw size={12} /> Refresh</button>
        </div>
        <div className="flex gap-2 mb-4">
          <div className="flex items-center gap-2 flex-1 border border-[#d3d3d3] px-3">
            <Search size={14} className="text-gray-400" />
            <input data-testid="user-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUsers()} placeholder="Search by name or email…" className="flex-1 py-2.5 text-sm outline-none" />
          </div>
          <button data-testid="user-search-btn" onClick={loadUsers} className="border border-[#0a2540] px-4 text-sm font-semibold hover:bg-[#0a2540] hover:text-white transition-colors">Search</button>
        </div>
        <div className="space-y-1.5">
          {users.map((u) => {
            const isSelf = u.id === meId;
            const locked = !isSuper && (u.role === "admin" || u.role === "superadmin");
            return (
              <div key={u.id} data-testid={`user-row-${u.email}`} className="flex items-center justify-between gap-3 border-b border-gray-100 py-2">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{u.name || u.email}</div>
                  <div className="text-xs text-[#475569] truncate">{u.email} · {u.plan}</div>
                </div>
                <select
                  data-testid={`role-select-${u.email}`}
                  value={u.role}
                  disabled={isSelf || locked}
                  onChange={(e) => changeRole(u.id, e.target.value)}
                  className={`text-sm border border-[#d3d3d3] px-2 py-1.5 outline-none focus:border-[#ff3b00] disabled:opacity-50 ${ROLE_COLORS[u.role] ? "" : ""}`}
                >
                  {[...new Set([u.role, ...roleOptions])].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            );
          })}
          {users.length === 0 && <div className="label-tech opacity-60 text-center py-6">No users found</div>}
        </div>
      </div>
    </div>
  );
}

function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const load = () => api.get("/admin/projects").then((r) => setProjects(r.data.projects)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this project permanently?")) return;
    await api.delete(`/admin/projects/${id}`);
    setProjects((p) => p.filter((x) => x.id !== id));
    toast.success("Project deleted");
  };

  return (
    <div className="bg-white border border-[#0a2540] shadow-hard p-5" data-testid="admin-projects-card">
      <div className="flex items-center justify-between mb-4">
        <div className="label-tech flex items-center gap-1.5"><LayoutGrid size={13} /> ALL PROJECTS ({projects.length})</div>
        <button onClick={load} className="label-tech flex items-center gap-1 hover:text-[#ff3b00]"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="space-y-1.5">
        {projects.map((p) => (
          <div key={p.id} data-testid={`admin-project-${p.id}`} className="flex items-center justify-between gap-3 border-b border-gray-100 py-2">
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{p.name} {p.published && <span className="label-tech text-[#166534] ml-1">LIVE</span>}</div>
              <div className="text-xs text-[#475569] truncate">{p.sections} sections · owner {p.owner_email || "—"}{p.customer_email ? ` · client ${p.customer_email}` : ""}</div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a href={`/preview/${p.id}`} target="_blank" rel="noreferrer" className="p-1.5 border border-[#d3d3d3] hover:border-[#0055ff] hover:text-[#0055ff]"><Globe size={13} /></a>
              <button onClick={() => remove(p.id)} className="p-1.5 border border-[#d3d3d3] hover:border-[#ff3b00] hover:text-[#ff3b00]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {projects.length === 0 && <div className="label-tech opacity-60 text-center py-6">No projects yet</div>}
      </div>
    </div>
  );
}
